import { ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { AdminFrame } from './AdminFrame';
import { ChecklistItem, errorText, fmtDate, fmtDateTime, rpc, SchoolDetail } from './api';
import { EnterSupportDialog, TIER_B_AVAILABLE, TierPicker } from './bits';
import { SchoolPills } from './pills';
import { checklistLabel } from './checklist';
import { langList } from '../teacher/readiness';
import { StaffTab } from './SchoolStaff';
import { ClassesTab } from './SchoolClasses';
import { SettingsTab } from './SchoolSettings';
import { AuditList } from './AuditList';

const TABS = ['overview', 'staff', 'classes', 'settings', 'activity'] as const;
export type SchoolTab = (typeof TABS)[number];

export type SchoolCtx = {
  detail: SchoolDetail;
  reload: () => Promise<void>;
  inSupport: boolean;
  askSupport: () => void;
};

// The school page (platform admin). Viewing is always allowed and never
// shows results. Changing anything INSIDE the school — staff, classes,
// settings — needs support mode (spec G1); platform-level actions —
// paperwork, suspend/resume, Sandbox → Customer, offboarding — don't.
export function SchoolPage() {
  const { t } = useI18n();
  const [title, setTitle] = useState<string>('');
  return (
    <AdminFrame section="schools" title={title || t['pa.school']}>
      {({ support, refreshSupport }) => (
        <SchoolBody onTitle={setTitle} supportSchool={support.active ? support.school_id : null} refreshSupport={refreshSupport} />
      )}
    </AdminFrame>
  );
}

function SchoolBody({
  onTitle,
  supportSchool,
  refreshSupport,
}: {
  onTitle: (s: string) => void;
  supportSchool: string | null;
  refreshSupport: () => Promise<void>;
}) {
  const { t } = useI18n();
  const { id = '', tab = 'overview' } = useParams();
  const [detail, setDetail] = useState<SchoolDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);

  const reload = useCallback(async () => {
    const r = await rpc<SchoolDetail>('platform_school_detail', { p_school_id: id });
    if (r.error) setError(errorText(t, r.error));
    else {
      setDetail(r.data);
      onTitle(r.data.school.name);
    }
  }, [id, t, onTitle]);

  useEffect(() => {
    reload();
  }, [reload, supportSchool]);

  if (error) {
    return (
      <section className="panel p5">
        <p className="error-text" role="alert">
          {error}
        </p>
        <p>
          <Link to="/admin/schools">{t['addSchool.backToList']}</Link>
        </p>
      </section>
    );
  }
  if (!detail) return <p role="status" className="text-muted">{t.loading}</p>;

  const s = detail.school;
  const active = (TABS as readonly string[]).includes(tab) ? (tab as SchoolTab) : 'overview';
  const ctx: SchoolCtx = { detail, reload, inSupport: detail.in_support, askSupport: () => setSupportOpen(true) };
  const tabLabel: Record<SchoolTab, string> = {
    overview: t['admin.navOverview'],
    staff: t['pa.tabStaff'](detail.counts.staff),
    classes: t['pa.tabClasses'](detail.counts.classes),
    settings: t['pa.tabSettings'],
    activity: t['pa.tabActivity'],
  };

  return (
    <>
      <p className="crumbs">
        <Link to="/admin">{t['admin.navOverview']}</Link> › <Link to="/admin/schools">{t['admin.navSchools']}</Link> ›{' '}
        <span aria-current="page">{s.name}</span>
      </p>

      <section className="panel p5 school-head" aria-label={t['pa.schoolSummary']}>
        <div className="school-head-main">
          <p className="school-head-code">
            <span className="code-chip">{s.code}</span>
          </p>
          <span className="pill-row">
            <SchoolPills school={s} />
            {detail.in_support && <Pill kind="warn">{t['pa.inSupportPill']}</Pill>}
          </span>
        </div>
        {s.state !== 'offboarded' &&
          (detail.in_support ? (
            <p className="form-hint">{t['pa.inSupportHint']}</p>
          ) : (
            <button type="button" className="btn btn-dark" onClick={() => setSupportOpen(true)}>
              {t['pa.enterSupport']}
            </button>
          ))}
      </section>

      <nav className="tabs" aria-label={t['pa.schoolSections']}>
        {TABS.map((k) => (
          <Link
            key={k}
            to={`/admin/schools/${s.id}${k === 'overview' ? '' : `/${k}`}`}
            className={`tabx${k === active ? ' on' : ''}`}
            aria-current={k === active ? 'page' : undefined}
          >
            {tabLabel[k]}
          </Link>
        ))}
      </nav>

      {active === 'overview' && <OverviewTab {...ctx} />}
      {/* Inside the school: people, classes and settings are shown only in
          support mode (spec G1; enforced by the database, 0010). */}
      {(active === 'staff' || active === 'classes' || active === 'settings') && !detail.in_support ? (
        <SupportOnly ctx={ctx} what={active === 'staff' ? t['pa.soStaff'] : active === 'classes' ? t['pa.soClasses'] : t['pa.soSettings']} />
      ) : (
        <>
          {active === 'staff' && <StaffTab {...ctx} />}
          {active === 'classes' && <ClassesTab {...ctx} />}
          {active === 'settings' && <SettingsTab {...ctx} />}
        </>
      )}
      {active === 'activity' && <AuditList scope="school" schoolId={s.id} title={t['pa.tabActivity']} />}

      <EnterSupportDialog
        open={supportOpen}
        schoolId={s.id}
        schoolName={s.name}
        defaultReason={s.school_type === 'sandbox' ? 'test_accounts' : 'school_asked'}
        onClose={() => setSupportOpen(false)}
        onEntered={async () => {
          setSupportOpen(false);
          await refreshSupport();
          await reload();
        }}
      />
    </>
  );
}

/** A whole tab or page that is inside the school: explained, with the way in. */
export function SupportOnly({ ctx, what }: { ctx: SchoolCtx; what: string }) {
  const { t } = useI18n();
  const offboarded = ctx.detail.school.state === 'offboarded';
  return (
    <section className="panel p5 support-only" aria-labelledby="so-title">
      <h2 className="panel-title" id="so-title">
        {offboarded ? t['pa.soOffboardedTitle'] : t['pa.soTitle']}
      </h2>
      <p>{offboarded ? t['pa.soOffboardedBody'] : t['pa.soBody'](what)}</p>
      {!offboarded && (
        <>
          <p className="form-hint">{t['pa.soHint']}</p>
          <div className="form-actions">
            <button type="button" className="btn btn-dark" onClick={ctx.askSupport}>
              {t['pa.enterSupport']}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/** Shown in place of a support-only action until support mode is on. */
export function SupportGate({ ctx, children, what }: { ctx: SchoolCtx; children: ReactNode; what: string }) {
  const { t } = useI18n();
  if (ctx.detail.school.state === 'offboarded') return null;
  if (ctx.inSupport) return <>{children}</>;
  return (
    <div className="support-gate">
      <p>{t['pa.supportNeeded'](what)}</p>
      <button type="button" className="btn btn-ghost" onClick={ctx.askSupport}>
        {t['pa.enterSupport']}
      </button>
    </div>
  );
}

function OverviewTab(ctx: SchoolCtx) {
  const { t, locale } = useI18n();
  const { detail } = ctx;
  const s = detail.school;
  const sandbox = s.school_type === 'sandbox';
  const [pwOpen, setPwOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState<null | 'notice_sent' | 'dpo_signoff'>(null);
  const [dedicatedOpen, setDedicatedOpen] = useState(false);
  const required = detail.checklist.filter((c) => c.level === 'required');
  const doneReq = required.filter((c) => c.done).length;

  const go = (k: ChecklistItem['key']): ReactNode => {
    const base = `/admin/schools/${s.id}`;
    switch (k) {
      case 'paperwork':
        return (
          <button type="button" className="link-btn" onClick={() => setPwOpen(true)}>
            {t['pa.changePaperwork']}
          </button>
        );
      case 'safety_net':
      case 'profile':
        return <Link to={`${base}/settings`}>{t['pa.goSettings']}</Link>;
      case 'classes':
      case 'teachers_assigned':
        return <Link to={`${base}/classes`}>{t['pa.goClasses']}</Link>;
      case 'staff':
        return <Link to={`${base}/staff`}>{t['pa.goStaff']}</Link>;
      case 'notice':
        return (
          <button type="button" className="link-btn" onClick={() => (ctx.inSupport ? setStepOpen('notice_sent') : ctx.askSupport())}>
            {detail.checklist.find((c) => c.key === 'notice')?.done ? t['pa.reopen'] : t['pa.markDone']}
          </button>
        );
      case 'dpo':
        return (
          <button type="button" className="link-btn" onClick={() => (ctx.inSupport ? setStepOpen('dpo_signoff') : ctx.askSupport())}>
            {detail.checklist.find((c) => c.key === 'dpo')?.done ? t['pa.reopen'] : t['pa.markDone']}
          </button>
        );
      case 'dedicated': {
        // Platform-level, like paperwork: no support mode needed.
        const item = detail.checklist.find((c) => c.key === 'dedicated');
        return item?.done ? (
          <span className="text-muted">{t['pa.dedicatedRef'](item.reference ?? '')}</span>
        ) : (
          <button type="button" className="link-btn" onClick={() => setDedicatedOpen(true)}>
            {t['pa.confirmDedicated']}
          </button>
        );
      }
    }
  };

  return (
    <>
      <div className="two-col">
        <section className="panel p5" aria-labelledby="ck-title">
          <div className="panel-head wrap">
            <h2 className="panel-title" id="ck-title">
              {t['pa.checklist']}
            </h2>
            <span className="text-muted">{t['pa.setupProgress'](doneReq, required.length)}</span>
          </div>
          <p className="form-hint">
            {s.state === 'setting_up' ? (sandbox ? t['pa.gateSandbox'] : t['pa.gateCustomer']) : t['pa.gateDone']}
          </p>
          <ul className="checklist">
            {detail.checklist.map((c) => (
              <li key={c.key} className={c.done ? 'done' : c.level === 'required' ? 'todo-req' : 'todo-warn'}>
                <span className="ck-mark" aria-hidden="true">
                  {c.done ? '✓' : c.level === 'required' ? '!' : '○'}
                </span>
                <span className="ck-text">
                  {checklistLabel(t, c.key, sandbox)}
                  {!c.done && <ChecklistDetail item={c} />}
                  <span className="visually-hidden">: </span>
                  <span className="ck-meta">
                    {c.done ? (
                      <Pill kind="good">{t['pa.done']}</Pill>
                    ) : c.level === 'required' ? (
                      <Pill kind="crit">{t['pa.requiredTodo']}</Pill>
                    ) : (
                      <Pill kind="warn">{t['pa.warningTodo']}</Pill>
                    )}
                  </span>
                </span>
                {s.state !== 'offboarded' && <span className="ck-action">{go(c.key)}</span>}
              </li>
            ))}
          </ul>
        </section>

        <section className="panel p5" aria-labelledby="about-title">
          <h2 className="panel-title" id="about-title">
            {t['pa.about']}
          </h2>
          <dl className="kv">
            <div>
              <dt>{t['pa.municipality']}</dt>
              <dd>{s.municipality_name ?? '–'}</dd>
            </div>
            <div>
              <dt>{t['pa.officialNumber']}</dt>
              <dd>{s.official_number ?? '–'}</dd>
            </div>
            <div>
              <dt>{t['pa.defaultLanguage']}</dt>
              <dd>{t[`pa.lang_${s.default_language}`]}</dd>
            </div>
            {!sandbox && (
              <div>
                <dt>{t['pa.tier']}</dt>
                <dd>{s.tier === 'tier_a' || s.tier === 'tier_b' ? t[`pa.${s.tier}Short`] : '–'}</dd>
              </div>
            )}
            <div>
              <dt>{sandbox ? t['pa.agreement'] : t['pa.dpa']}</dt>
              <dd>
                {s.paperwork_status === 'signed'
                  ? t['pa.signedBy'](s.paperwork_signatory ?? '', fmtDate(locale, s.paperwork_signed_on))
                  : s.paperwork_status === 'sent'
                    ? t['pa.pwSent']
                    : t['pa.pwNotStarted']}
                {s.paperwork_note && <span className="text-muted"> · {t['r4.noteShown'](s.paperwork_note)}</span>}
              </dd>
            </div>
            {sandbox && s.state !== 'offboarded' && (
              <div>
                <dt>{t['r4.demo.title']}</dt>
                <dd>
                  <DemoSetting {...ctx} />
                </dd>
              </div>
            )}
            {s.contract_reference && (
              <div>
                <dt>{t['pa.contractRef']}</dt>
                <dd>{s.contract_reference}</dd>
              </div>
            )}
            <div>
              <dt>{t['pa.created']}</dt>
              <dd>{fmtDate(locale, s.created_at)}</dd>
            </div>
            <div>
              <dt>{t['pa.staff']}</dt>
              <dd>
                {detail.counts.staff}
                {detail.counts.test_accounts > 0 && ` · ${t['pa.nTest'](detail.counts.test_accounts)}`}
              </dd>
            </div>
            <div>
              <dt>{t['pa.classes']}</dt>
              <dd>
                {detail.counts.classes}
                {detail.counts.classes_without_teacher > 0 && ` · ${t['pa.nNoTeacher'](detail.counts.classes_without_teacher)}`}
              </dd>
            </div>
            <div>
              <dt>{t['pa.primaryHead']}</dt>
              <dd>{detail.has_primary_head ? t['pa.yes'] : <Pill kind="warn">{t['pa.missing']}</Pill>}</dd>
            </div>
            <div>
              <dt>{t['pa.schoolAdmins']}</dt>
              <dd>{detail.school_admins > 0 ? detail.school_admins : <Pill kind="warn">{t['pa.missing']}</Pill>}</dd>
            </div>
          </dl>
        </section>
      </div>

      <Lifecycle {...ctx} />

      {detail.support_history.length > 0 && (
        <section className="panel p5" aria-labelledby="sh-title">
          <h2 className="panel-title" id="sh-title">
            {t['pa.supportHistory']}
          </h2>
          <ul className="school-list">
            {detail.support_history.map((h) => (
              <li key={h.started_at}>
                <span>
                  {fmtDateTime(locale, h.started_at)} · {h.by ?? '–'}
                </span>
                <span className="text-muted">{t[`pa.sr_${h.reason as 'other'}`] ?? h.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PaperworkDialog open={pwOpen} ctx={ctx} onClose={() => setPwOpen(false)} />
      <SetupStepDialog step={stepOpen} ctx={ctx} onClose={() => setStepOpen(null)} />
      <DedicatedDialog open={dedicatedOpen} ctx={ctx} onClose={() => setDedicatedOpen(false)} />
    </>
  );
}

// Tier B (0013): Zencera confirms the school's dedicated database is set up.
function DedicatedDialog({ open, ctx, onClose }: { open: boolean; ctx: SchoolCtx; onClose: () => void }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const r = await rpc<{ state: string }>('platform_confirm_dedicated', { p_school_id: s.id, p_reference: reference });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(r.data.state === 'live' && s.state !== 'live' ? t['pa.nowLive'] : t['pa.dedicatedConfirmed']);
    setReference('');
    onClose();
    await ctx.reload();
  }

  return (
    <Dialog
      open={open}
      title={t['pa.dedicatedTitle']}
      intro={<p className="form-hint">{t['pa.dedicatedIntro']}</p>}
      submitLabel={t['pa.confirmDedicated']}
      submitDisabled={!reference.trim()}
      busy={busy}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-ref`}>
          {t['pa.dedicatedRefLabel']}
        </label>
        <input
          id={`${id}-ref`}
          className="field"
          value={reference}
          maxLength={200}
          onChange={(e) => setReference(e.target.value)}
          aria-describedby={`${id}-refhint`}
          autoComplete="off"
        />
        <p className="form-hint" id={`${id}-refhint`}>
          {t['pa.dedicatedRefHint']}
        </p>
      </div>
    </Dialog>
  );
}

function PaperworkDialog({ open, ctx, onClose }: { open: boolean; ctx: SchoolCtx; onClose: () => void }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [status, setStatus] = useState(s.paperwork_status);
  const [date, setDate] = useState(s.paperwork_signed_on ?? '');
  const [who, setWho] = useState(s.paperwork_signatory ?? '');
  const [note, setNote] = useState(s.paperwork_note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);

  async function save() {
    setBusy(true);
    setError(null);
    const r = await rpc<{ state: string }>('platform_set_paperwork', {
      p_school_id: s.id,
      p_status: status,
      p_signed_on: status === 'signed' ? date || null : null,
      p_signatory: status === 'signed' ? who : null,
      p_note: note.trim() || null,
    });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(r.data.state === 'live' && s.state !== 'live' ? t['pa.nowLive'] : t['pa.saved']);
    onClose();
    await ctx.reload();
  }

  return (
    <Dialog
      open={open}
      title={s.school_type === 'sandbox' ? t['pa.agreement'] : t['pa.dpa']}
      intro={<p className="form-hint">{t['pa.paperworkOnlyZencera']}</p>}
      submitLabel={t['pa.save']}
      busy={busy}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <fieldset className="inline-radios">
        <legend className="field-label">{t['pa.status']}</legend>
        {(['not_started', 'sent', 'signed'] as const).map((v) => (
          <label key={v}>
            <input type="radio" name={`${id}-pw`} checked={status === v} onChange={() => setStatus(v)} />{' '}
            {v === 'signed' ? t['pa.pwSigned'] : v === 'sent' ? t['pa.pwSent'] : t['pa.pwNotStarted']}
          </label>
        ))}
      </fieldset>
      {status === 'signed' && (
        <>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-d`}>
              {t['pa.signedOn']}
            </label>
            <input id={`${id}-d`} className="field" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-w`}>
              {t['pa.signatory']}
            </label>
            <input id={`${id}-w`} className="field" value={who} onChange={(e) => setWho(e.target.value)} autoComplete="off" />
          </div>
        </>
      )}
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-note`}>
          {t['r4.paperNote']}
        </label>
        <input id={`${id}-note`} className="field" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)}
          aria-describedby={`${id}-note-h`} autoComplete="off" />
        <p className="form-hint" id={`${id}-note-h`}>{t['r4.paperNoteHint']}</p>
      </div>
    </Dialog>
  );
}

function SetupStepDialog({
  step,
  ctx,
  onClose,
}: {
  step: null | 'notice_sent' | 'dpo_signoff';
  ctx: SchoolCtx;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = !!(step && ctx.detail.school.setup_steps[step]);

  async function save() {
    if (!step) return;
    if (step === 'dpo_signoff' && !done && !note.trim()) return setError(t['r4.dpoNeedsName']);
    setBusy(true);
    setError(null);
    const r = await rpc<{ state: string }>('platform_set_setup_step', {
      p_school_id: ctx.detail.school.id,
      p_step: step,
      p_done: !done,
      p_note: note || null,
    });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(r.data.state === 'live' && ctx.detail.school.state !== 'live' ? t['pa.nowLive'] : t['pa.saved']);
    setNote('');
    onClose();
    await ctx.reload();
  }

  return (
    <Dialog
      open={!!step}
      title={step === 'notice_sent' ? t['pa.ckNotice'] : t['pa.ckDpo']}
      intro={<p className="form-hint">{done ? t['pa.reopenIntro'] : t['pa.markDoneIntro']}</p>}
      submitLabel={done ? t['pa.reopen'] : t['pa.markDone']}
      busy={busy}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      {!done && (
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-n`}>
            {step === 'dpo_signoff' ? t['r4.dpoName'] : t['pa.noteOptional']}
          </label>
          <input id={`${id}-n`} className="field" value={note} onChange={(e) => setNote(e.target.value)}
            required={step === 'dpo_signoff'} aria-describedby={step === 'dpo_signoff' ? `${id}-nh` : undefined} />
          {step === 'dpo_signoff' && <p className="form-hint" id={`${id}-nh`}>{t['r4.dpoNameHint']}</p>}
        </div>
      )}
    </Dialog>
  );
}

// 0020: what is still missing, not checked, or a Draft, under a checklist item.
function ChecklistDetail({ item }: { item: ChecklistItem }) {
  const { t } = useI18n();
  const list = (codes: string[]) => langList(t, codes);
  if (item.missing && item.missing.length > 0) return <span className="ck-detail"> · {t['r4.ready.missingLangs'](list(item.missing))}</span>;
  if (item.unreviewed && item.unreviewed.length > 0) return <span className="ck-detail"> · {t['r4.ck.unreviewed'](list(item.unreviewed))}</span>;
  if (item.draft && item.draft.length > 0) return <span className="ck-detail"> · {t['r4.ck.draft'](list(item.draft))}</span>;
  return null;
}

// K2: a Sandbox school is a trial school (DPO review and notice required) or
// the demo school (no real students; those two are warnings). Logged.
function DemoSetting(ctx: SchoolCtx) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const r = await rpc('platform_set_demo_school', { p_school_id: s.id, p_is_demo: !s.is_demo, p_reason: reason });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(t['r4.demo.saved']);
    setOpen(false);
    setReason('');
    await ctx.reload();
  }

  return (
    <>
      {s.is_demo && <Pill kind="neutral">{t['r4.demo.pill']}</Pill>} <span>{s.is_demo ? t['r4.demo.isDemo'] : t['r4.demo.isTrial']}</span>{' '}
      <button type="button" className="link-btn" onClick={() => setOpen(true)}>
        {s.is_demo ? t['r4.demo.makeTrial'] : t['r4.demo.makeDemo']}
      </button>
      <Dialog
        open={open}
        title={s.is_demo ? t['r4.demo.makeTrial'] : t['r4.demo.makeDemo']}
        intro={<p className="form-hint">{s.is_demo ? t['r4.demo.isTrial'] : t['r4.demo.isDemo']}</p>}
        submitLabel={t['pa.save']}
        busy={busy}
        error={error}
        onSubmit={save}
        onClose={() => setOpen(false)}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-r`}>
            {t['r4.demo.reason']}
          </label>
          <input id={`${id}-r`} className="field" value={reason} onChange={(e) => setReason(e.target.value)} required minLength={5} />
        </div>
      </Dialog>
    </>
  );
}

const SUSPEND_REASONS = ['contract_lapse', 'data_protection', 'sandbox_ended', 'other'] as const;

function Lifecycle(ctx: SchoolCtx) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const id = useId();
  const s = ctx.detail.school;
  const [open, setOpen] = useState<null | 'suspend' | 'resume' | 'convert' | 'ack' | 'offboard'>(null);
  const [reason, setReason] = useState<string>('contract_lapse');
  const [message, setMessage] = useState('');
  const [contract, setContract] = useState('');
  // Tier A by default while Tier B isn't offered (0014).
  const [tier, setTier] = useState(TIER_B_AVAILABLE ? '' : 'tier_a');
  const [ackName, setAckName] = useState(s.paperwork_signatory ?? '');
  const [ackDate, setAckDate] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const holdOver = s.offboard_after ? new Date(s.offboard_after).getTime() <= Date.now() : false;

  function close() {
    setOpen(null);
    setError(null);
    setTyped('');
  }

  async function run(fn: string, args: Record<string, unknown>, done: string) {
    setBusy(true);
    setError(null);
    const r = await rpc(fn, args);
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(done);
    close();
    await ctx.reload();
  }

  if (s.state === 'offboarded') {
    return (
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <h2 className="panel-title" id={`${id}-t`}>
          {t['pa.lifecycle']}
        </h2>
        <p>{t['pa.offboardedOn'](fmtDate(locale, s.offboarded_at))}</p>
        <p>
          <Link to={`/admin/archive?school=${s.id}&kind=offboarding_report`}>{t['pa.seeReport']}</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['pa.lifecycle']}
      </h2>
      <p className="form-hint">{t['pa.lifecycleHint']}</p>
      {s.state === 'suspended' ? (
        <>
          <p>
            {t['pa.suspendedSince'](fmtDate(locale, s.state_changed_at))}
            {s.suspension_reason && ` · ${t[`pa.sus_${s.suspension_reason as 'other'}`]}`}
          </p>
          <ol className="offboard-steps">
            <li className={holdOver ? 'done' : ''}>{t['pa.offStepHold'](fmtDate(locale, s.offboard_after))}</li>
            <li className={s.offboarding_ack ? 'done' : ''}>
              {s.offboarding_ack ? t['pa.offStepAckDone'](s.offboarding_ack.name, fmtDate(locale, s.offboarding_ack.on)) : t['pa.offStepAck']}
            </li>
            <li>{t['pa.offStepConfirm']}</li>
          </ol>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={() => setOpen('resume')}>
              {t['pa.resume']}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen('ack')}>
              {t['pa.recordAck']}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => setOpen('offboard')} disabled={!holdOver || !s.offboarding_ack}>
              {t['pa.offboard']}
            </button>
          </div>
        </>
      ) : (
        <div className="form-actions">
          {s.school_type === 'sandbox' && (
            <button type="button" className="btn btn-ghost" onClick={() => setOpen('convert')}>
              {t['pa.convert']}
            </button>
          )}
          <button type="button" className="btn btn-danger" onClick={() => setOpen('suspend')}>
            {t['pa.suspend']}
          </button>
        </div>
      )}

      <Dialog
        open={open === 'suspend'}
        title={t['pa.suspendTitle'](s.name)}
        intro={<p className="form-hint">{t['pa.suspendIntro']}</p>}
        submitLabel={t['pa.suspend']}
        danger
        busy={busy}
        error={error}
        onSubmit={() =>
          run('platform_suspend_school', { p_school_id: s.id, p_reason: reason, p_message: message || null }, t['pa.suspendedDone'])
        }
        onClose={close}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-sr`}>
            {t['pa.reason']}
          </label>
          <span className="select-wrap">
            <select id={`${id}-sr`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
              {SUSPEND_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t[`pa.sus_${r}`]}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-sm`}>
            {t['pa.suspendMessage']}
          </label>
          <textarea id={`${id}-sm`} className="field" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} aria-describedby={`${id}-smh`} />
          <p className="form-hint" id={`${id}-smh`}>
            {t['pa.suspendMessageHint']}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={open === 'resume'}
        title={t['pa.resumeTitle'](s.name)}
        intro={<p className="form-hint">{t['pa.resumeIntro']}</p>}
        submitLabel={t['pa.resume']}
        busy={busy}
        error={error}
        onSubmit={() => run('platform_resume_school', { p_school_id: s.id }, t['pa.resumedDone'])}
        onClose={close}
      />

      <Dialog
        open={open === 'convert'}
        title={t['pa.convertTitle']}
        intro={
          <>
            <p className="form-hint">{t['pa.convertIntro']}</p>
            {ctx.detail.counts.test_accounts > 0 && <p className="notice">{t['pa.convertTestsFirst'](ctx.detail.counts.test_accounts)}</p>}
          </>
        }
        submitLabel={t['pa.convert']}
        busy={busy}
        error={error}
        onSubmit={() =>
          run(
            'platform_convert_to_customer',
            { p_school_id: s.id, p_reason: message, p_contract_reference: contract, p_tier: tier },
            t['pa.convertedDone'],
          )
        }
        onClose={close}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-cr`}>
            {t['pa.reason']}
          </label>
          <input id={`${id}-cr`} className="field" value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-cc`}>
            {t['pa.contractRef']}
          </label>
          <input id={`${id}-cc`} className="field" value={contract} onChange={(e) => setContract(e.target.value)} />
        </div>
        <TierPicker value={tier} onChange={setTier} />
      </Dialog>

      <Dialog
        open={open === 'ack'}
        title={t['pa.recordAck']}
        intro={
          <>
            <p className="form-hint">{t['pa.ackIntro']}</p>
            <blockquote className="quote">{t['pa.ackText']}</blockquote>
          </>
        }
        submitLabel={t['pa.save']}
        busy={busy}
        error={error}
        onSubmit={() =>
          run('platform_record_offboarding_ack', { p_school_id: s.id, p_head_name: ackName, p_on: ackDate || null, p_note: message || null }, t['pa.saved'])
        }
        onClose={close}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-an`}>
            {t['pa.ackBy']}
          </label>
          <input id={`${id}-an`} className="field" value={ackName} onChange={(e) => setAckName(e.target.value)} autoComplete="off" />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-ad`}>
            {t['pa.ackOn']}
          </label>
          <input id={`${id}-ad`} className="field" type="date" max={today} value={ackDate} onChange={(e) => setAckDate(e.target.value)} />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-anote`}>
            {t['pa.noteOptional']}
          </label>
          <input id={`${id}-anote`} className="field" value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
      </Dialog>

      <Dialog
        open={open === 'offboard'}
        title={t['pa.offboardTitle'](s.name)}
        intro={
          <>
            <p className="form-hint">{t['pa.offboardIntro']}</p>
            <ul className="bullets">
              <li>{t['pa.offboardErase'](ctx.detail.counts.staff)}</li>
              <li>{t['pa.offboardKeep']}</li>
              <li>{t['pa.offboardReport']}</li>
            </ul>
          </>
        }
        submitLabel={t['pa.offboard']}
        danger
        submitDisabled={typed.trim().toUpperCase() !== s.code}
        busy={busy}
        error={error}
        onSubmit={async () => {
          setBusy(true);
          const r = await rpc('platform_offboard_school', { p_school_id: s.id, p_typed_code: typed });
          setBusy(false);
          if (r.error) return setError(errorText(t, r.error));
          announce(t['pa.offboardedDone']);
          close();
          navigate(`/admin/archive?school=${s.id}&kind=offboarding_report`);
        }}
        onClose={close}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-tc`}>
            {t['pa.typeCode'](s.code)}
          </label>
          <input id={`${id}-tc`} className="field" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </div>
      </Dialog>
    </section>
  );
}
